import React, { useState } from 'react';
import { X, Key, Globe, Building2, CheckCircle, ChevronRight } from 'lucide-react';
import { oauthApi } from '../lib/api';
import type { CFWorkspace, OAuthConnection } from '../lib/api';
import { Button, Spinner } from './ui';

interface ConnectClickFunnelsModalProps {
  onClose: () => void;
  onConnected: (connection: OAuthConnection) => void;
}

type Step = 'credentials' | 'workspace' | 'done';

export default function ConnectClickFunnelsModal({ onClose, onConnected }: ConnectClickFunnelsModalProps) {
  const [step, setStep] = useState<Step>('credentials');
  const [apiToken, setApiToken] = useState('');
  const [subdomain, setSubdomain] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Workspace selection
  const [workspaces, setWorkspaces] = useState<CFWorkspace[]>([]);
  const [selectedWorkspace, setSelectedWorkspace] = useState<CFWorkspace | null>(null);

  const handleFetchWorkspaces = async () => {
    if (!apiToken.trim() || !subdomain.trim()) {
      setError('API token and workspace subdomain are required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await oauthApi.fetchWorkspaces(apiToken.trim(), subdomain.trim());

      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to fetch workspaces');
      }

      const wsData = response.data as unknown as { workspaces: CFWorkspace[] };
      const wsList = wsData.workspaces || [];

      if (wsList.length === 0) {
        throw new Error('No workspaces found. Check your API token and subdomain.');
      }

      setWorkspaces(wsList);

      // Auto-select if only one workspace
      if (wsList.length === 1) {
        setSelectedWorkspace(wsList[0]);
      }

      setStep('workspace');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch workspaces');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!selectedWorkspace) return;

    setLoading(true);
    setError(null);

    try {
      const response = await oauthApi.connect({
        apiToken: apiToken.trim(),
        workspaceId: selectedWorkspace.id,
        workspaceName: selectedWorkspace.name,
        workspaceSubdomain: selectedWorkspace.subdomain,
        teamId: selectedWorkspace.teamId,
        teamName: selectedWorkspace.teamName,
      });

      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to save connection');
      }

      const connData = response.data as unknown as { connection: OAuthConnection };
      setStep('done');
      setTimeout(() => {
        onConnected(connData.connection);
        onClose();
      }, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save connection');
    } finally {
      setLoading(false);
    }
  };

  const stepNumber = step === 'credentials' ? 1 : step === 'workspace' ? 2 : 3;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="ds-card w-full max-w-lg shadow-card animate-fade">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-line">
          <div>
            <h3 className="font-display text-xl font-semibold text-ink">Connect ClickFunnels</h3>
            <p className="text-sm text-ink-3 mt-1">Step {stepNumber} of 2</p>
          </div>
          <button
            onClick={onClose}
            className="text-ink-3 hover:text-ink transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Progress bar */}
        <div className="px-6 pt-4">
          <div className="flex items-center space-x-2">
            {['credentials', 'workspace'].map((s, i) => (
              <React.Fragment key={s}>
                <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                  stepNumber > i + 1 ? 'bg-success-soft text-success' :
                  stepNumber === i + 1 ? 'bg-accent text-white' :
                  'bg-surface-2 text-ink-3'
                }`}>
                  {stepNumber > i + 1 ? <CheckCircle className="w-5 h-5" /> : i + 1}
                </div>
                {i < 1 && (
                  <div className={`flex-1 h-0.5 ${stepNumber > i + 1 ? 'bg-success' : 'bg-line'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-danger-soft border border-danger/30 rounded-[11px]">
              <p className="text-sm text-danger">{error}</p>
            </div>
          )}

          {/* Step 1: API Token + Subdomain */}
          {step === 'credentials' && (
            <div className="space-y-4">
              <p className="text-sm text-ink-2">
                Enter your ClickFunnels API token and workspace subdomain. You can find your API token in
                ClickFunnels under Settings &rarr; Integrations &rarr; API Access Tokens.
              </p>

              <div>
                <label className="ds-label">
                  <Key className="w-4 h-4 inline mr-1" />
                  API Token
                </label>
                <input
                  type="password"
                  value={apiToken}
                  onChange={(e) => setApiToken(e.target.value)}
                  className="ds-input"
                  placeholder="Enter your ClickFunnels API token"
                />
              </div>

              <div>
                <label className="ds-label">
                  <Globe className="w-4 h-4 inline mr-1" />
                  Workspace Subdomain
                </label>
                <div className="flex items-center">
                  <input
                    type="text"
                    value={subdomain}
                    onChange={(e) => setSubdomain(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                    className="ds-input flex-1 rounded-r-none"
                    placeholder="myworkspace"
                  />
                  <span className="px-3 py-2 bg-surface-2 border border-l-0 border-line rounded-r-[11px] text-sm text-ink-3">
                    .myclickfunnels.com
                  </span>
                </div>
              </div>

              <Button
                onClick={handleFetchWorkspaces}
                disabled={loading || !apiToken.trim() || !subdomain.trim()}
                className="w-full"
              >
                {loading ? (
                  <Spinner className="!h-5 !w-5" />
                ) : (
                  <>
                    Fetch Workspaces
                    <ChevronRight className="w-4 h-4" />
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Step 2: Select Workspace + Save */}
          {step === 'workspace' && (
            <div className="space-y-4">
              <p className="text-sm text-ink-2">
                Select the workspace you want to connect.
              </p>

              <div>
                <label className="ds-label">
                  <Building2 className="w-4 h-4 inline mr-1" />
                  Workspace
                </label>
                <select
                  value={selectedWorkspace ? String(selectedWorkspace.id) : ''}
                  onChange={(e) => {
                    const ws = workspaces.find((w) => String(w.id) === e.target.value);
                    setSelectedWorkspace(ws || null);
                  }}
                  className="ds-input"
                >
                  <option value="">Select a workspace...</option>
                  {workspaces.map((ws) => (
                    <option key={ws.id} value={String(ws.id)}>
                      {ws.name} ({ws.subdomain}.myclickfunnels.com) — {ws.teamName}
                    </option>
                  ))}
                </select>
              </div>

              {selectedWorkspace && (
                <div className="p-3 bg-accent-soft rounded-[11px]">
                  <p className="text-sm font-medium text-accent-ink">{selectedWorkspace.name}</p>
                  <p className="text-xs text-accent-ink/80 mt-1">
                    Team: {selectedWorkspace.teamName} &middot; {selectedWorkspace.subdomain}.myclickfunnels.com
                  </p>
                </div>
              )}

              <div className="flex space-x-3">
                <Button
                  variant="ghost"
                  onClick={() => { setStep('credentials'); setError(null); }}
                  className="flex-1"
                >
                  Back
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={loading || !selectedWorkspace}
                  className="flex-1"
                >
                  {loading ? (
                    <Spinner className="!h-5 !w-5" />
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      Save Connection
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Done */}
          {step === 'done' && (
            <div className="text-center py-6">
              <CheckCircle className="w-16 h-16 text-success mx-auto mb-4" />
              <h4 className="font-display text-lg font-semibold text-ink">Connection Saved</h4>
              <p className="text-sm text-ink-2 mt-1">
                {selectedWorkspace?.name} has been connected successfully.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
